import { z } from 'zod';
import { ORDER_STATUSES } from './order.js';
import { PENNYLANE_INVOICE_STATUSES } from './pennylane.js';
/**
 * Statut affiché dans la liste **Commandes**, qui mélange commandes Shopify et
 * factures Pennylane (décision du 10/09/2026). C'est la seule échelle de
 * statuts que voit l'utilisateur : les états internes de la chaîne d'envoi
 * (`scheduled`, `preparing`, `generated`, `queued`) s'y replient tous sur
 * « À transmettre », et une facture Pennylane parcourt la même échelle avec
 * moins d'étapes.
 *
 * Le parcours nominal : À transmettre → Transmise → En préparation → Préparée
 * → Expédiée. À côté : En erreur, Bloquée, Traitée manuellement et Ignorée.
 * Une étape calculée en mode test reçoit un indicateur « Simulé » séparé.
 */
export const UNIFIED_ORDER_STATUSES = [
    'to_transmit',
    'transmitted',
    'integrated',
    'prepared',
    'shipped',
    'error',
    'blocked',
    'handled',
    'ignored',
];
export const unifiedOrderStatusSchema = z.enum(UNIFIED_ORDER_STATUSES);
export const UNIFIED_ORDER_STATUS = {
    to_transmit: {
        code: 'to_transmit',
        label: 'À transmettre',
        tone: 'gray',
        description: 'Reçue, pas encore déposée chez Martelet.',
    },
    transmitted: {
        code: 'transmitted',
        label: 'Transmise',
        tone: 'blue',
        description: 'CSV déposé sur le FTP Martelet.',
    },
    integrated: {
        code: 'integrated',
        label: 'En préparation',
        tone: 'blue',
        description: 'Martelet a intégré la commande dans son système.',
    },
    prepared: {
        code: 'prepared',
        label: 'Préparée',
        tone: 'blue',
        description: 'Martelet a préparé le colis ; expédition à venir.',
    },
    shipped: {
        code: 'shipped',
        label: 'Expédiée',
        tone: 'green',
        description: 'Colis expédié, numéro de suivi remonté dans Shopify.',
    },
    error: {
        code: 'error',
        label: 'En erreur',
        tone: 'red',
        description: 'Échec de préparation ou d’envoi après épuisement des tentatives.',
    },
    blocked: {
        code: 'blocked',
        label: 'Bloquée',
        tone: 'red',
        description: 'Traitement manuel requis.',
    },
    handled: {
        code: 'handled',
        label: 'Traitée manuellement',
        tone: 'orange',
        description: 'Déclarée traitée hors plateforme.',
    },
    ignored: {
        code: 'ignored',
        label: 'Ignorée',
        tone: 'gray',
        description: 'Non envoyée volontairement.',
    },
};
/** Statut unifié d'une commande Shopify. */
export function unifiedStatusFromOrder(status) {
    switch (status) {
        case 'scheduled':
        case 'preparing':
        case 'generated':
        case 'queued':
            return 'to_transmit';
        case 'sent':
        case 'shadow':
            return 'transmitted';
        case 'integrated':
            return 'integrated';
        case 'prepared':
            return 'prepared';
        case 'shipped':
            return 'shipped';
        case 'error':
            return 'error';
        case 'blocked':
            return 'blocked';
        case 'handled':
            return 'handled';
        case 'ignored':
            return 'ignored';
    }
}
/** Statut unifié d'une facture Pennylane. */
export function unifiedStatusFromInvoice(status) {
    switch (status) {
        case 'pending':
        case 'queued':
            return 'to_transmit';
        case 'sent':
            return 'transmitted';
        case 'ignored':
            return 'ignored';
        case 'error':
            return 'error';
    }
}
function invert(sources, toUnified) {
    const map = Object.fromEntries(UNIFIED_ORDER_STATUSES.map((code) => [code, []]));
    for (const source of sources) {
        map[toUnified(source)].push(source);
    }
    return map;
}
/**
 * Expansion d'un statut unifié vers les statuts `orders.status` qu'il couvre —
 * c'est ce que la liste unifiée pousse dans le `$in` Mongo. Un tableau vide
 * signifie « aucune commande Shopify ne peut porter ce statut » (le côté
 * Shopify de la requête est alors sauté).
 */
export const ORDER_STATUSES_BY_UNIFIED = invert(ORDER_STATUSES, unifiedStatusFromOrder);
/** Même expansion pour `pennylaneInvoices.status`. */
export const INVOICE_STATUSES_BY_UNIFIED = invert(PENNYLANE_INVOICE_STATUSES, unifiedStatusFromInvoice);
//# sourceMappingURL=unified-order.js.map